import Axios from '@/utils/axios';
import {
  getBackendErrorMessage,
  getBackendErrorRows,
} from '@/utils/errorHandler';
import {
  ApproveBody,
  CloseBody,
  CreatePurchaseOrderBody,
  GetPurchaseOrdersParams,
  GetReceiveItemsParams,
  Paginated,
  PurchaseOrder,
  PurchaseOrderDetail,
  PurchaseReceiveItem,
  ReceiveItemRowError,
  ReceivePurchaseItemsBody,
  RejectBody,
} from '@/types/purchase-order';

const BASE = '/v1/purchase-orders';

export const getPurchaseOrders = async (
  params: GetPurchaseOrdersParams = {}
): Promise<Paginated<PurchaseOrder>> => {
  try {
    const response = await Axios.get(BASE, {
      params: {
        ...params,
        statuses: params.statuses?.join(','),
        businessUnits: params.businessUnits?.join(','),
        entities: params.entities?.join(','),
      },
    });
    return response.data; // { data, pagination }
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getPurchaseOrderById = async (
  id: string
): Promise<PurchaseOrderDetail> => {
  try {
    const response = await Axios.get(`${BASE}/${id}`);
    return response.data;
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const getPurchaseOrderReceiveItems = async (
  id: string,
  params: GetReceiveItemsParams = {}
): Promise<Paginated<PurchaseReceiveItem>> => {
  try {
    const response = await Axios.get(`${BASE}/${id}/receive-items`, { params });
    return response.data; // { data, pagination }
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const createPurchaseOrder = async (
  body: CreatePurchaseOrderBody
): Promise<{ id: string }> => {
  try {
    const response = await Axios.post(BASE, body);
    return response.data.data; // unwrap { status, data: { id } }
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const approvePurchaseOrder = async (
  id: string,
  body: ApproveBody
): Promise<void> => {
  try {
    await Axios.post(`${BASE}/${id}/approve`, body);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const cooApprovePurchaseOrder = async (
  id: string,
  body: ApproveBody
): Promise<void> => {
  try {
    await Axios.post(`${BASE}/${id}/coo-approve`, body);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

export const rejectPurchaseOrder = async (
  id: string,
  body: RejectBody
): Promise<void> => {
  try {
    await Axios.post(`${BASE}/${id}/reject`, body);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};

// Carries the backend's per-row rejections so the form can highlight the exact
// fields. A plain Error would flatten a 200-row batch into one sentence.
export class ReceivePurchaseItemsError extends Error {
  rows: ReceiveItemRowError[];

  constructor(message: string, rows: ReceiveItemRowError[]) {
    super(message);
    this.name = 'ReceivePurchaseItemsError';
    this.rows = rows;
  }
}

export const receivePurchaseItems = async (
  id: string,
  body: ReceivePurchaseItemsBody
): Promise<void> => {
  try {
    await Axios.post(`${BASE}/${id}/receive`, body);
  } catch (error: unknown) {
    const rows = getBackendErrorRows<ReceiveItemRowError>(error);

    if (rows) {
      throw new ReceivePurchaseItemsError(getBackendErrorMessage(error), rows);
    }

    throw new Error(getBackendErrorMessage(error));
  }
};

export const closePurchaseOrder = async (
  id: string,
  body: CloseBody
): Promise<void> => {
  try {
    await Axios.post(`${BASE}/${id}/close`, body);
  } catch (error: unknown) {
    throw new Error(getBackendErrorMessage(error));
  }
};
